import {
  Box,
  Stack,
  Button,
  Tooltip,
  Divider,
  useTheme,
  IconButton,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";
import { Edit, OpenInNew, Share, List } from "@material-ui/icons";
import { useListingsMatchContext } from "contexts/ListingsMatchContext";
import { PATH_PUBLIC } from "routes/paths";
import { preventBubbling } from "utils/listings/helper";
import { getUserFullNameFallBackToPhoneNumber } from "utils/Haim/utils";
import CopyClipboard from "components/CopyClipboard";
import { useModal } from "hooks/useModal";
import ListingsMatchFavoriteListEditDialog from "./ListingsMatchFavoriteListEditDialog";
import { Favorite } from "@material-ui/icons";
import type { RealEstateContact } from "../../../@types/sharedSchema";

type ListingMatchContactListingsBottomOverlayProps = {
  contactData: RealEstateContact | null;
  selectedListingsCount: number;
  onAddSelectedToFavorites?: () => void;
  onAddSelectedToList?: () => void;
};

export default function ListingMatchContactListingsBottomOverlay({
  contactData,
  selectedListingsCount,
  onAddSelectedToFavorites,
  onAddSelectedToList
}: ListingMatchContactListingsBottomOverlayProps): JSX.Element {
  const { t } = useTranslation();

  const { open, openModal, closeModal } = useModal();

  const { favoriteListingsOfPickedContact, searchState } = useListingsMatchContext();
  const { pickedContact } = searchState;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const backgroundColor = "white";
  const shadowColor = theme.palette.grey[300];
  const textColor = theme.palette.secondary.darker;

  const hasListings = favoriteListingsOfPickedContact?.listingIds?.length;

  const renderNameSection = () => (
    <Typography variant="h6" sx={{ color: textColor }}>
      {getUserFullNameFallBackToPhoneNumber(pickedContact)}
    </Typography>
  );

  const renderAddingSelectedSection = () => (
    <>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          bgcolor: theme.palette.secondary.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: "0.75rem",
          fontWeight: "bold"
        }}
      >
        {selectedListingsCount}
      </Box>
      <Typography variant="caption" color="textSecondary">
        {t("listingsMatch.propertiesSelected")}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        onClick={onAddSelectedToList}
        startIcon={<List />}
        sx={{
          borderColor: theme.palette.grey[400],
          color: theme.palette.text.primary,
          "&:hover": {
            borderColor: theme.palette.grey[600]
          }
        }}
      >
        {t("listingsMatch.addSelectedToList")}
      </Button>
      {pickedContact && (
        <Button
          variant="contained"
          size="small"
          onClick={onAddSelectedToFavorites}
          startIcon={<Favorite />}
          sx={{
            bgcolor: theme.palette.secondary.dark,
            color: "white",
            "&:hover": {
              bgcolor: theme.palette.secondary.darker
            }
          }}
        >
          {t("listingMatchToContactsPage.control.addToMiniSite")}
        </Button>
      )}
    </>
  );

  const renderFavoriteCountAndControlsSection = () => (
    <>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          bgcolor: theme.palette.secondary.dark,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: "0.75rem",
          fontWeight: "bold",
          cursor: "pointer"
        }}
        onClick={openModal}
      >
        {favoriteListingsOfPickedContact?.listingIds?.length || 0}
      </Box>
      <Typography variant="caption" color="textSecondary">
        {t("listingsMatch.listingsMatchedToContact")}
      </Typography>
      <ListingMatchFavoriteControls
        minisiteId={favoriteListingsOfPickedContact?.id}
        disabled={!hasListings}
        phoneNumber={favoriteListingsOfPickedContact?.contactId}
        editFavoriteListings={
          <Tooltip title={t("general.edit")}>
            <IconButton disabled={!hasListings} color="secondary" onClick={openModal}>
              <Edit />
            </IconButton>
          </Tooltip>
        }
      />
    </>
  );

  return (
    <Box
      sx={{
        borderRadius: "10px 10px 0 0",
        boxShadow: 2,
        backgroundColor,
        position: "fixed",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "auto",
        paddingY: 1,
        paddingX: 2,
        zIndex: 100,
        "&::before": {
          content: '""',
          borderRadius: "10px 10px 0 0",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          boxShadow: "0 0 5px 5px " + shadowColor
        }
      }}
    >
      <ListingsMatchFavoriteListEditDialog
        open={open}
        contactData={contactData}
        favoriteListingsOfContact={favoriteListingsOfPickedContact}
        onClose={closeModal}
      />

      {isMobile ? (
        // Mobile layout - 3 lines
        <>
          <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
            {renderNameSection()}
          </Stack>
          {selectedListingsCount > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ mb: 1, whiteSpace: "nowrap" }}
            >
              {renderAddingSelectedSection()}
            </Stack>
          )}
          {pickedContact && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ whiteSpace: "nowrap" }}
            >
              {renderFavoriteCountAndControlsSection()}
            </Stack>
          )}
        </>
      ) : (
        // Desktop layout - single line
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ whiteSpace: "nowrap" }}
        >
          {pickedContact && (
            <>
              {renderNameSection()}
              <Divider orientation="vertical" flexItem />
            </>
          )}
          {selectedListingsCount > 0 && (
            <>
              {renderAddingSelectedSection()}
              {pickedContact && <Divider orientation="vertical" flexItem />}
            </>
          )}
          {pickedContact && <>{renderFavoriteCountAndControlsSection()}</>}
        </Stack>
      )}
    </Box>
  );
}

type ListingMatchFavoriteControlsProps = {
  children?: ReactNode;
  showSortBy?: boolean;
  editFavoriteListings?: JSX.Element;
  minisiteId: string;
  disabled: boolean;
  iconSize?: number;
  phoneNumber?: string;
};

export function ListingMatchFavoriteControls({
  children,
  editFavoriteListings,
  minisiteId,
  disabled,
  phoneNumber,
  iconSize
}: ListingMatchFavoriteControlsProps): JSX.Element {
  const { t: translate } = useTranslation();

  const listingLink = `${window.location.origin}${PATH_PUBLIC.myListings}/${minisiteId}`;

  let whatsAppLink = "";

  if (phoneNumber) {
    whatsAppLink = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${listingLink}`;
  } else {
    whatsAppLink = `https://api.whatsapp.com/send?text=${listingLink}`;
  }

  const content = (
    <Stack direction="row" gap={1} justifyContent="center">
      <Box onClick={preventBubbling()}>
        <CopyClipboard value={listingLink} disabled={disabled} iconSize={iconSize} />
      </Box>
      {editFavoriteListings && editFavoriteListings}
      <Tooltip title={translate("general.open")}>
        <IconButton
          disabled={disabled}
          color="secondary"
          href={listingLink}
          target="_blank"
          sx={{ fontSize: iconSize }}
          onClick={preventBubbling(null, false)}
        >
          <OpenInNew sx={{ fontSize: iconSize }} />
        </IconButton>
      </Tooltip>
      <Tooltip title={translate("general.share")}>
        <IconButton
          href={whatsAppLink}
          color="secondary"
          target="_blank"
          disabled={disabled}
          onClick={preventBubbling(null, false)}
        >
          <Share sx={{ fontSize: iconSize }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  if (children) {
    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="center"
        alignItems="center"
        gap={2}
      >
        {content}
        {children}
      </Stack>
    );
  }

  return content;
}
